import type {
  DocAccess,
  DocStatus,
  DocType,
  DocVersionMetadata,
} from "@xop/dashboard-shared";

import { serializeFaqMarkdown, type FaqPair } from "../FaqFieldsEditor.js";
import { toMetadata as videoToMetadata, type VideoFields } from "../VideoFieldsEditor.js";

/**
 * Form state + the pure create/update payload builder for the shared document
 * create/edit form, extracted from `DocumentManualForm.tsx`. `composePayload`
 * used to be nested inside the component (closing over `video`/`faq`/`faqRaw`);
 * lifting it out + taking those as an explicit `ctx` makes it directly
 * unit-testable.
 */

export const DOC_TYPES: { value: DocType; label: string; hint: string }[] = [
  { value: "article", label: "Article",  hint: "Standard markdown article (default)." },
  { value: "guide",   label: "Guide",    hint: "Long-form tutorial / how-to." },
  { value: "video",   label: "Video",    hint: "Plays YouTube-style on the customer portal." },
  { value: "faq",     label: "FAQ",      hint: "Q&A list with accordion + search." },
  { value: "slide",   label: "Slides",   hint: "Deck or slideshow attachment." },
  { value: "file",    label: "File",     hint: "Any other downloadable file." },
];

export interface FormState {
  title: string;
  status: DocStatus;
  doc_type: DocType;
  document_category_id: string;
  /** Solution-area ids the doc is tagged with (many-to-many). Empty = none. */
  solution_area_ids: number[];
  default_release_id: string;
  content: string;
  /** Who the document is FOR, library-wide. Defaults to `general` so a new
   * document behaves exactly as every existing one does — narrowing is a
   * deliberate act, never a side effect of the editor gaining a field. */
  access_scope: DocAccess["scope"];
  /** Product codes this doc is scoped to. Meaningful only when
   * `access_scope === "product"`, but KEPT when the scope changes: an
   * author who flips to `general` to check something and back should not
   * silently lose their tagging. */
  product_codes: string[];
  /** Product-feature codes (CRM slugs). Meaningful when
   * `access_scope === "feature"`; kept across scope flips like the others. */
  feature_codes: string[];
  release_ids: number[];
}

export const EMPTY: FormState = {
  title: "",
  status: "draft",
  doc_type: "article",
  document_category_id: "",
  solution_area_ids: [],
  default_release_id: "",
  content: "",
  access_scope: "general",
  product_codes: [],
  feature_codes: [],
  release_ids: [],
};

export interface ComposedPayload {
  document: {
    title: string;
    status: DocStatus;
    doc_type: DocType;
    document_category_id: number | null;
    default_release_id: number | null;
    external_url?: string | null;
    thumbnail_url?: string | null;
  };
  content: string;
  metadata: DocVersionMetadata | null;
  /** Solution-area ids, TOP-LEVEL (parallels product_codes / release_ids) —
   * the portal-api controller reconciles the many-to-many join from this. */
  solution_area_ids: number[];
  /** Access targeting, also TOP-LEVEL — read by portal-api's
   * `access_control_params` and applied by `Documents::ApplyAccessControl`.
   *
   * The mapping arrays are sent on EVERY write, including when the scope is
   * not `product` / `release`. Sending them unconditionally is what makes
   * "switch from product to general" actually clear the mapping, and the
   * reconcile is keyed on the key being present — omitting them would leave
   * a stale product tag quietly gating a document nobody thinks is gated. */
  access_scope: DocAccess["scope"];
  product_codes: string[];
  feature_codes: string[];
  release_ids: number[];
}

/** Compose the content + metadata + document fields that go in the
 * create / update payload. Pure — the video / FAQ editor state is passed in
 * via `ctx` rather than closed over. */
export function composePayload(
  state: FormState,
  ctx: { video: VideoFields; faqRaw: boolean; faq: FaqPair[] },
): ComposedPayload {
  const document_category_id = state.document_category_id
    ? Number(state.document_category_id)
    : null;
  const default_release_id = state.default_release_id
    ? Number(state.default_release_id)
    : null;
  const solution_area_ids = state.solution_area_ids;
  const access = {
    access_scope: state.access_scope,
    product_codes: state.access_scope === "product" ? state.product_codes : [],
    feature_codes: state.access_scope === "feature" ? state.feature_codes : [],
    release_ids: state.access_scope === "release" ? state.release_ids : [],
  };

  if (state.doc_type === "video") {
    // For video, the user-supplied state.content is treated as the
    // optional description body shown below the player.
    return {
      document: {
        title: state.title,
        status: state.status,
        doc_type: "video",
        document_category_id,
        default_release_id,
        external_url: ctx.video.kind === "upload" ? null : (ctx.video.external_url.trim() || null),
        thumbnail_url: ctx.video.thumbnail_url.trim() || null,
      },
      content: state.content,
      metadata: videoToMetadata(ctx.video),
      solution_area_ids,
      ...access,
    };
  }

  if (state.doc_type === "faq" && !ctx.faqRaw) {
    return {
      document: {
        title: state.title,
        status: state.status,
        doc_type: "faq",
        document_category_id,
        default_release_id,
      },
      content: serializeFaqMarkdown(ctx.faq),
      metadata: null,
      solution_area_ids,
      ...access,
    };
  }

  return {
    document: {
      title: state.title,
      status: state.status,
      doc_type: state.doc_type,
      document_category_id,
      default_release_id,
    },
    content: state.content,
    metadata: null,
    solution_area_ids,
    ...access,
  };
}
